import createCache from '@emotion/cache'
import { CacheProvider, Global } from '@emotion/react'
import CloseIcon from '@mui/icons-material/Close'
import { chipClasses } from '@mui/material/Chip'
import { dialogClasses } from '@mui/material/Dialog'
import { createTheme as createMuiTheme, responsiveFontSizes, Theme, ThemeProvider } from '@mui/material/styles'
import rtlPlugin from '@mui/stylis-plugin-rtl'
import React, { ReactElement, ReactNode, useMemo } from 'react'
import { prefixer } from 'stylis'

import { parseQueryParams, ThemeType } from 'shared'
import { UiDirectionType } from 'translations'

import buildConfig from '../constants/buildConfig'
import { BREAKPOINTS } from '../constants/layout'
import { prepareTypography } from '../constants/typography'
import useLocalStorage, { THEME_STORAGE_KEY } from '../hooks/useLocalStorage'
import globalStyle from '../styles/global/GlobalStyle'
import { muiShadowCreator } from '../utils/muiShadowCreator'

const ltrCache = createCache({
  key: 'mui-ltr-cache',
  stylisPlugins: [prefixer],
})

const rtlCache = createCache({
  key: 'mui-rtl-cache',
  stylisPlugins: [prefixer, rtlPlugin],
})

const createTheme = (themeType: ThemeType, contentDirection: UiDirectionType): Omit<Theme, 'toggleTheme'> => {
  const isContrast = themeType === 'contrast'
  const theme = isContrast ? buildConfig().darkTheme : buildConfig().lightTheme
  const typography = prepareTypography(buildConfig().fonts)

  return responsiveFontSizes(
    createMuiTheme({
      contentDirection,
      isContrastTheme: isContrast,
      breakpoints: {
        values: BREAKPOINTS,
      },
      direction: contentDirection,
      shadows: muiShadowCreator(themeType),
      typography,
      palette: theme.palette,
      components: {
        MuiTypography: {
          defaultProps: {
            dir: 'auto',
            variantMapping: {
              h1: 'h1',
              h2: 'h2',
              h3: 'h3',
              h4: 'h4',
              h5: 'h5',
              h6: 'h6',
              subtitle1: 'span',
              subtitle2: 'span',
              body1: 'span',
              body2: 'span',
            },
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              backgroundColor: 'unset',
            },
          },
        },
        MuiAccordionSummary: {
          defaultProps: {
            disableRipple: false,
          },
        },
        MuiListSubheader: {
          styleOverrides: {
            root: {
              backgroundColor: theme.palette.background.default,
            },
          },
        },
        MuiListItemIcon: {
          // MUI v9 reduced the default to theme.spacing(4.5) (36px), which is narrower than our 40px
          // list icons and leaves no space between icon and text. Restore the previous 56px.
          styleOverrides: {
            root: {
              minWidth: 56,
            },
          },
        },
        MuiChip: {
          defaultProps: {
            deleteIcon: <CloseIcon />,
          },
          styleOverrides: {
            icon: {
              color: theme.palette.tertiary.light,
            },
            deleteIcon: {
              color: theme.palette.tertiary.light,
            },
            outlined: {
              backgroundColor: theme.palette.background.accent,
              borderColor: theme.palette.tertiary.light,

              [`&.${chipClasses.clickable}`]: {
                ':hover': {
                  backgroundColor: theme.palette.background.default,
                },
              },
              [`&.${chipClasses.focusVisible}`]: {
                outline: `2px solid ${theme.palette.tertiary.dark}`,
                backgroundColor: theme.palette.background.default,
              },
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              borderRadius: 16,
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              [`&:not(.${dialogClasses.paperFullScreen})`]: {
                borderRadius: 12,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
              },
            },
          },
        },
        MuiBreadcrumbs: {
          defaultProps: {
            dir: contentDirection,
          },
        },
        MuiTooltip: {
          defaultProps: {
            arrow: true,
            slotProps: {
              tooltip: {
                dir: 'auto',
              },
            },
          },
          styleOverrides: {
            popper: {
              padding: '8px',
            },
            arrow: {
              color: theme.palette.primary.main,
            },
            tooltip: {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              fontSize: typography.body2.fontSize,
              padding: '8px 16px',
            },
          },
        },
        // Multiline TextField labels are not displayed correctly on iOS devices
        // https://github.com/digitalfabrik/integreat-app/issues/4058
        // https://github.com/mui/material-ui/issues/46945#issuecomment-3299699965
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              // Fix gap in search box border, see #3600
              '& legend': {
                letterSpacing: 0,
              },
            },
            notchedOutline: {
              '@supports (-webkit-appearance: none)': {
                '& legend': {
                  visibility: 'visible !important',
                },
              },
            },
            input: {
              '&::placeholder': {
                opacity: 0.8,
              },
            },
          },
        },
      },
    }),
    { disableAlign: true },
  )
}

type ThemeContainerProps = {
  children: ReactNode
  contentDirection: UiDirectionType
}

const ThemeContainer = ({ children, contentDirection }: ThemeContainerProps): ReactElement => {
  const url = new URL(window.location.href)
  const { theme: queryTheme } = parseQueryParams(url.searchParams)
  const [storageThemeType, setStorageThemeType] = useLocalStorage<ThemeType>({
    key: THEME_STORAGE_KEY,
    initialValue: 'light',
  })

  const themeType = queryTheme ?? storageThemeType

  const theme = useMemo(() => {
    const toggleTheme = () => setStorageThemeType(themeType === 'light' ? 'contrast' : 'light')
    return { ...createTheme(themeType, contentDirection), toggleTheme }
  }, [contentDirection, themeType, setStorageThemeType])

  return (
    <CacheProvider value={contentDirection === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <Global styles={globalStyle({ theme })} />
        {children}
      </ThemeProvider>
    </CacheProvider>
  )
}

export default ThemeContainer
